import { useUserStore } from '@/stores/userStore'
import type { AuthTokenResponse } from '@/gen/types/AuthTokenResponse'

export type RequestConfig<T = unknown> = {
  method?: string
  url?: string
  baseURL?: string
  data?: T | FormData
  params?: Record<string, unknown>
  headers?: Record<string, string>
  signal?: AbortSignal
  // internal flag - prevents infinite 401→refresh→401 loops on retried requests
  _isRetryWithRefreshedAccessToken?: boolean
}

export type ResponseConfig<T = unknown> = {
  data: T
  status: number
  statusText: string
  headers: Record<string, string>
}

export class HttpError<TErr = unknown> extends Error {
  readonly response: {
    status: number
    statusText: string
    data: TErr
    headers: Record<string, string>
  }

  constructor(
    message: string,
    response: { status: number; statusText: string; data: TErr; headers: Record<string, string> }
  ) {
    super(message)
    this.name = 'HttpError'
    this.response = response
  }
}

/** @public */
export type ResponseErrorConfig<TErr = unknown> = HttpError<TErr>

export type Client = <TData = unknown, _TError = unknown, TVariables = unknown>(
  config: RequestConfig<TVariables>
) => Promise<ResponseConfig<TData>>

export const customParamsSerializer = (params: Record<string, unknown>): string => {
  const parts: string[] = []
  const encode = (val: string | number | boolean) => encodeURIComponent(val).replace(/%20/g, '+')

  const serialize = (obj: Record<string, unknown>, prefix?: string) => {
    Object.keys(obj).forEach(key => {
      const value = obj[key]
      const paramKey = prefix ? `${prefix}.${key}` : key

      if (value === null || typeof value === 'undefined') return

      if (Array.isArray(value)) {
        value.forEach(v => {
          parts.push(`${encode(paramKey)}=${encode(v as string | number | boolean)}`)
        })
      } else if (typeof value === 'object') {
        serialize(value as Record<string, unknown>, paramKey)
      } else {
        parts.push(`${encode(paramKey)}=${encode(value as string | number | boolean)}`)
      }
    })
  }

  serialize(params)
  return parts.join('&')
}

async function parseResponseBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? ''
  if (contentType.includes('application/json')) {
    return response.json()
  }
  const text = await response.text()
  return text || null
}

function collectHeaders(response: Response): Record<string, string> {
  const result: Record<string, string> = {}
  response.headers.forEach((value, key) => {
    result[key] = value
  })
  return result
}

// shared refreshPromise - if 2+ API calls yield 401, they share a promise as to not race each other.
let refreshPromise: Promise<AuthTokenResponse> | null = null

// uses raw fetch instead of clientFn - going through the interceptor would cause a loop
function attemptTokenRefresh(baseURL: string): Promise<AuthTokenResponse> {
  if (!refreshPromise) {
    refreshPromise = fetch(`${baseURL}/auth/refresh`, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      // credentials: 'include' sends refreshToken cookie on every request
      credentials: 'include'
    })
      .then(res => {
        if (!res.ok) throw new Error(`Refresh failed: ${res.status}`)
        return res.json() as Promise<AuthTokenResponse>
      })
      .finally(() => {
        refreshPromise = null
      })
  }
  return refreshPromise
}

export function createFetchClient(authorizationHeader?: string): Client {
  // clientFn allows recursion for retry with refreshed token
  const clientFn: Client = async <TData = unknown, _TError = unknown, TVariables = unknown>(
    config: RequestConfig<TVariables>
  ): Promise<ResponseConfig<TData>> => {
    const userStore = useUserStore()
    const baseURL = (import.meta.env.VITE_NRAI_BASE_URL as string) ?? ''
    const base = (config.baseURL ?? baseURL).replace(/\/$/, '')
    const path = (config.url ?? '').replace(/^\//, '')
    let url = path ? `${base}/${path}` : base

    if (config.params) {
      const qs = customParamsSerializer(config.params)
      if (qs) url += `?${qs}`
    }

    const token =
      authorizationHeader ?? (userStore.accessToken ? `Bearer ${userStore.accessToken}` : undefined)

    const headers: Record<string, string> = {
      'X-Requested-With': 'XMLHttpRequest',
      ...config.headers
    }
    if (token) headers['Authorization'] = token

    let body: string | FormData | undefined
    if (config.data !== undefined) {
      if (config.data instanceof FormData) {
        body = config.data
      } else {
        body = JSON.stringify(config.data)
        if (!headers['Content-Type']) headers['Content-Type'] = 'application/json'
      }
    }

    const response = await fetch(url, {
      method: (config.method ?? 'GET').toUpperCase(),
      headers,
      body,
      signal: config.signal,
      credentials: 'include'
    })

    const responseHeaders = collectHeaders(response)
    const data = await parseResponseBody(response)

    // 401 interceptor - on success: refreshes token and retries. on failure: logs out and falls through to throw below.
    if (
      response.status === 401 &&
      // eslint-disable-next-line no-underscore-dangle
      !config._isRetryWithRefreshedAccessToken &&
      !config.url?.includes('/auth/refresh') &&
      !config.url?.includes('/auth/revoke')
    ) {
      // If the token changed since this request was sent, retry with the current one
      const requestBearerToken = headers['Authorization']
      const currentStoreToken = userStore.accessToken
        ? `Bearer ${userStore.accessToken}`
        : undefined

      if (currentStoreToken && requestBearerToken !== currentStoreToken) {
        return clientFn({ ...config, _isRetryWithRefreshedAccessToken: true })
      }

      try {
        const tokenResponse = await attemptTokenRefresh(base)
        if (tokenResponse.accessToken) {
          userStore.updateTokens(tokenResponse.accessToken)
          // no await - retry errors propagate to caller, catch only covers the refresh itself
          return clientFn({ ...config, _isRetryWithRefreshedAccessToken: true })
        } else {
          throw new Error('unreachable')
        }
      } catch {
        await userStore.logout()
      }
    }

    if (!response.ok) {
      throw new HttpError<_TError>(`HTTP ${response.status} ${response.statusText}`, {
        status: response.status,
        statusText: response.statusText,
        data: data as _TError,
        headers: responseHeaders
      })
    }

    return {
      data: data as TData,
      status: response.status,
      statusText: response.statusText,
      headers: responseHeaders
    }
  }
  return clientFn
}

export function isHttpError<TErr = unknown>(error: unknown): error is HttpError<TErr> {
  return error instanceof HttpError
}

const client: Client = createFetchClient()

export default client
